import React from 'react';
import Sidenav from 'examples/Sidenav';
import { useMaterialUIController, setMiniSidenav } from 'context/index';
import routes from 'routes';
import DashboardLayout from 'examples/LayoutContainers/DashboardLayout';
import DashboardNavbar from 'examples/Navbars/DashboardNavbar';
import FallbackUI from './general/FallbackUI';
import cdccLogo from '../assets/img/logo_with_text.png';
import { ErrorBoundary } from 'react-error-boundary';
import { useAppContext } from 'common/AppContext';
import { TypeOfFile, projectFilesToAccept } from './file-upload/fileUpload.type';
import { ROUTES } from 'common/constants/urls';
import UploadDialog from './file-upload/UploadDialog';

interface LayoutProps {
  children: React.ReactNode;
  pageTitle?: string;
  pageHeader?: string;
  showSidenav?: boolean;
  showNavbar?: boolean;
}

const Layout: React.FC<LayoutProps> = ({ children, pageTitle, pageHeader: pageHeaderFromRoute, showSidenav = true, showNavbar = true }) => {
  const [fileUploadDialogOpen, setFileUploadDialogOpen] = React.useState(false);

  const openFileUploadDialog = () => setFileUploadDialogOpen(true);
  const closeFileUploadDialog = () => setFileUploadDialogOpen(false);

  const [controller, dispatch] = useMaterialUIController();
  const { miniSidenav, direction, layout, sidenavColor, transparentSidenav, whiteSidenav, darkMode } = controller;
  const [onMouseEnter, setOnMouseEnter] = React.useState(false);
  const { pageHeaderText } = useAppContext();
  const pageHeader = pageHeaderText == '' ? pageHeaderFromRoute : pageHeaderText;
  const prevPageHeaderRef = React.useRef<string | undefined>(pageHeader);

  // Collapse sidebar only when first entering payapp, limited report, or magic report flow
  React.useEffect(() => {
    const isPayappOrReport =
      pageHeader?.startsWith('Process payapp') || pageHeader?.startsWith('Limited Report') || pageHeader?.includes('Magic Report');
    const wasPayappOrReport =
      prevPageHeaderRef.current?.startsWith('Process payapp') ||
      prevPageHeaderRef.current?.startsWith('Limited Report') ||
      prevPageHeaderRef.current?.includes('Magic Report');

    if (isPayappOrReport && !wasPayappOrReport) {
      setMiniSidenav(dispatch, true);
    }

    prevPageHeaderRef.current = pageHeader;
  }, [pageHeader, dispatch]);

  // Open sidenav when mouse enter on mini sidenav
  const handleOnMouseEnter = () => {
    if (miniSidenav && !onMouseEnter) {
      setMiniSidenav(dispatch, false);
      setOnMouseEnter(true);
    }
  };

  // Close sidenav when mouse leave mini sidenav
  const handleOnMouseLeave = () => {
    setMiniSidenav(dispatch, true);
    setOnMouseEnter(false);
  };

  const navbarButtons = [
    {
      text: 'Toggle Sidebar',
      onClick: () => setMiniSidenav(dispatch, !miniSidenav), // Toggle sidebar state
      icon: miniSidenav ? 'menu' : 'menu_open',
    },
    {
      text: 'Upload Project',
      onClick: openFileUploadDialog,
      icon: 'file_upload',
    },
  ];

  const sidebarRoutes = [
    ...routes, // existing routes
    {
      type: 'collapse', // or a suitable type for your UI
      name: 'Upload File',
      icon: 'file_upload',
      key: 'upload-project',
      action: openFileUploadDialog, // action to open the FileUploadDialog
    },
  ];

  return (
    <React.Fragment>
      <ErrorBoundary FallbackComponent={FallbackUI}>
        {showSidenav && (
          <Sidenav
            color={sidenavColor}
            brand={cdccLogo}
            brandName=''
            routes={sidebarRoutes}
            miniSidenav={miniSidenav}
            onMouseEnter={handleOnMouseEnter}
            onMouseLeave={handleOnMouseLeave}
          />
        )}
        {children && (
          <DashboardLayout>
            {showNavbar && <DashboardNavbar navbarButtons={navbarButtons} title={pageTitle} pageHeader={pageHeader} />}
            {children}
            <UploadDialog
              open={fileUploadDialogOpen}
              onClose={closeFileUploadDialog}
              filesToAccept={projectFilesToAccept}
              typeOfFile={TypeOfFile.Project}
              navigatePath={ROUTES.PROJECTS}
            />
          </DashboardLayout>
        )}
      </ErrorBoundary>
    </React.Fragment>
  );
};

export default Layout;
